<template>
    <main
        v-if="data"
        class="relative pt-25 md:pt-36">
        <TheBreadcrumbs :items="breadcrumbs" />

        <ProductPreview
            v-if="data?.data.images.length"
            :product="productPreviewItem"
            :direction-slug="directionSlug"
            :calculator
            class="mt-5" />

        <LazyApiSection
            min-height="20rem"
            @visible="loadDeferredProductContent">
            <DiagramFlow
                v-if="diagram && deferredProduct?.category.data"
                :initial-data="diagram"
                :model-range="modelRange?.data"
                :category="deferredProduct.category.data"
                :current-product-slug="productSlug"
                class="my-10 md:my-20" />

            <section
                v-if="deferredProduct?.blocks?.data?.length"
                class="container">
                <CatalogProduct
                    :items="deferredProduct.blocks.data"
                    class="py-10 md:py-20" />
            </section>

            <FaqSection
                v-if="deferredProduct?.faqs?.data?.length"
                title-class="lg:!text-32/100"
                :title="`Часто задаваемые вопросы про ${data?.data.feedback_label || data?.data.name}`"
                :questions="deferredProduct.faqs.data" />

            <EquipmentSection
                v-if="deferredProduct?.other_products?.data"
                title="Другие товары этой категории"
                :items="deferredProduct.other_products.data"
                class="my-10 md:my-20" />
        </LazyApiSection>

        <RemainingQuestionsSection class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import { getCalculator } from '~/services/calculator.service'
import type { ICalculator } from '~/types/calculator.types'
import {
    DirectionsSlugAsConst,
    type TDirectionSlug,
} from '~/types/global.types'
import type { ProductLinePage, ProductPage } from '~/types/pages.types'

import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import CatalogProduct from '~/components/pages/catalog/catalog-product/CatalogProduct.vue'
import EquipmentSection from '~/components/pages/directions/equipment-section/EquipmentSection.vue'
import FaqSection from '~/components/pages/directions/faq-section/FaqSection.vue'
import DiagramFlow from '~/components/particles/diagram-flow/DiagramFlow.vue'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import ProductPreview from '~/components/particles/product-preview/ProductPreview.vue'
import type { ProductPreviewProps } from '~/components/particles/product-preview/product-preview.types'
import RemainingQuestionsSection from '~/components/particles/remaining-questions-section/RemainingQuestionsSection.vue'

import { stripHtml } from '~/utils/formats'
import {
    defineSeoAdditinalProperties,
    defineSeoAggregateRating,
} from '~/utils/seo'

const route = useRoute()
const nuxtApp = useNuxtApp()
const modalStore = useModalStore()
const productSlug = computed(() => String(route.params.product || ''))

const { data, error } = await useApiFetch<Page<ProductPage>>(
    `/v1/products/${route.params.product}`,
    {
        query: {
            include: ['category.parent_category', 'pages', 'calculator'].join(
                ','
            ),
        },
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        useLastModified: true,
    }
)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

const productCategory = computed(() => data.value?.data.category?.data)
const modelRangeParentSlug = computed(
    () => productCategory.value?.page?.parent_slug
)
const modelRangeSlug = computed(() => productCategory.value?.page?.slug)

const modelRange = ref<Page<ProductLinePage> | null>(null)

if (modelRangeParentSlug.value && modelRangeSlug.value) {
    const { data: modelRangeData } = await useApiFetch<Page<ProductLinePage>>(
        `/v1/categories/${modelRangeParentSlug.value}/products/${modelRangeSlug.value}`,
        {
            key: `modelRange-${modelRangeParentSlug.value}-${modelRangeSlug.value}`,
            getCachedData(key) {
                return nuxtApp.payload?.data?.[key]
            },
        }
    )

    modelRange.value = modelRangeData.value
}

const { data: deferredProductData, execute: executeDeferredProductContent } =
    await useApiFetch<Page<ProductPage>>(
        `/v1/products/${route.params.product}`,
        {
            key: `deferred-product-${route.params.product}`,
            query: {
                include: [
                    'blocks',
                    'faqs',
                    'category.parent_category',
                    'pages',
                    'calculator',
                    'other_products',
                    'diagramNodeRelation.parent.products',
                    'diagramNodeRelation.children.products',
                ].join(','),
            },
            server: false,
            immediate: false,
            watch: false,
            getCachedData(key) {
                return nuxtApp.payload?.data?.[key]
            },
        }
    )

const { data: diagram, execute: executeDiagram } = await useApiFetch<
    Page<ProductPage>
>(`/v1/system-diagrams/${data.value?.data.id}`, {
    key: `deferred-product-diagram-${data.value?.data.id}`,
    query: {
        include: [
            'diagramNodeRelation',
            'diagramNodeRelation.parent.products',
            'diagramNodeRelation.children.products',
        ].join(','),
    },
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
    server: false,
    immediate: false,
    watch: false,
})

const isDeferredProductContentLoading = ref(false)
const deferredProduct = computed(() => deferredProductData.value?.data)

async function loadDeferredProductContent() {
    if (isDeferredProductContentLoading.value || deferredProductData.value) {
        return
    }

    isDeferredProductContentLoading.value = true

    await Promise.all([executeDeferredProductContent(), executeDiagram()])

    isDeferredProductContentLoading.value = false
}

const directionSlug = computed<TDirectionSlug | null>(() => {
    const slug = productCategory.value?.parent_slug || ''

    const direction = slug
        .replace('radioguides', 'radioguide')
        .replace('audioguides', 'audioguide')
        .replace('dostupnaia-sreda', 'dostupsreda')
        .replace('cabins', 'cabin')
        .replace('conference-systems', 'conference-system')
        .replace('interpretation', 'sinhroperevod')

    return DirectionsSlugAsConst.includes(direction as TDirectionSlug)
        ? (direction as TDirectionSlug)
        : null
})

const calculatorId = computed<number>(() => {
    switch (directionSlug.value) {
        case 'radioguide':
        case 'dostupsreda': {
            return 2
        }
        case 'audioguide': {
            return 1
        }
        case 'cabin': {
            return 4
        }
        case 'conference-system': {
            return 5
        }
        case 'sinhroperevod': {
            return 3
        }
        default: {
            return 0
        }
    }
})

const calculatorData = ref<Page<ICalculator> | null>(null)

if (calculatorId.value > 0) {
    const { data: fetchedCalculatorData } = await useApiFetch<
        Page<ICalculator>
    >(`/v1/calculators/${calculatorId.value}`, {
        key: directionSlug.value || undefined,
        query: {
            include: [
                'charging_batteries',
                'type',
                'storage_bag',
                'headphones',
                'languages_translation_quantity',
                'cabin_type',
                'forced_ventilation',
                'case',
                'transportation_bag',
                'charging_batteries.product',
                'type.product',
                'storage_bag.product',
                'headphones.product',
                'languages_translation_quantity.product',
                'cabin_type.product',
                'forced_ventilation.product',
                'case.product',
                'transportation_bag.product',
            ].join(','),
        },
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
    })

    calculatorData.value = fetchedCalculatorData.value
}

const calculator = computed(() =>
    directionSlug.value
        ? getCalculator(
              directionSlug.value === 'dostupsreda'
                  ? 'radioguide'
                  : directionSlug.value,
              calculatorData.value?.data || null
          )
        : null
)

if (data.value?.data) {
    modalStore.setHeaderFeedbackLabel(data.value.data?.feedback_label || '')
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
    modalStore.setNameProduct(data.value.data?.feedback_label || '')
}

const productPreviewItem = computed<ProductPreviewProps | null>(
    (): ProductPreviewProps | null => {
        if (!data.value) return null

        return {
            feedback_label: data.value.data.feedback_label,
            slug: data.value.data.slug,
            max_cart_quantity: data.value.data.max_cart_quantity || undefined,
            modalButtonStretch: true,
            modalButtonText: 'Заказать',
            focus: 'always',
            videoLink: data.value.data.link_rutube || undefined,
            videoPreview: data.value.data.rutube_preview
                ? data.value.data.rutube_preview[0]
                : undefined,
            estimated_cost: '',
            images: data.value.data.images || [],
            title: data.value.data.name,
            parameters: data.value.data.parameter || undefined,
            price: data.value.data.price || undefined,
            isAvailable: data.value.data.is_available,
            description: data.value.data.description || undefined,
        }
    }
)

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Cromi', to: '/' }]

    const category = data.value?.data?.category?.data

    //  Товары, у которых есть привязка к направлению и модельному ряду
    if (category?.page.parent_slug && category?.page.slug) {
        //  Направление
        items.push({
            label: `${category.parent_category?.data.name}`,
            to: `/${category.parent_category?.data.page.slug}/`,
        })

        //  Модельный ряд
        items.push({
            label: `${category.name}`,
            to: `/${category.page.parent_slug}/${category.page.slug}/`,
        })
    } else if (category?.parent_category?.data) {
        //  Товары, у которых нет привязки к модельному ряду, но есть направление

        items.push({
            label: `Каталог`,
            to: '/catalog/',
        })

        items.push({
            label: `${category?.name}`,
            to: `/catalog/#sub-${category?.slug}`,
        })
    } else {
        //  Товары, у которых нет направления и модельного ряда

        items.push({
            label: `Каталог`,
            to: '/catalog/',
        })

        items.push({
            label: `${category?.name}`,
            to: `/catalog/#${category?.slug}`,
        })
    }

    // Товар
    items.push({
        label: `${data.value?.data.name}`,
    })

    return items
})

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })
}

// const organization = getSeoOrganization()
if (data.value) {
    useSchemaOrg([
        defineProduct({
            name: data.value.data.name,
            description: stripHtml(data.value.data.description),
            url: route.path,
            image: data.value.data.images.map((image) =>
                defineSeoImage({
                    ...image,
                    url: getMediaLink(image.url) || image.url,
                })
            ),
            additionalProperty: defineSeoAdditinalProperties(
                data.value.data.parameter
            ),
            sku: data.value.data.id,
            brand: {
                '@type': 'Brand',
                name: data.value.data.brand ? data.value.data.brand : 'Cromi',
            },
            offers: defineOffer({
                price: data.value.data.price,
                priceCurrency: 'RUB',
                availability: 'https://schema.org/InStock',
                // shippingDetails: defineShippingDetails(),

                // seller: defineOrganization(organization),
            }),
            aggregateRating: defineSeoAggregateRating(),
        }),
    ])
}
</script>
